"use client";

import {
  Badge,
  Box,
  Button,
  HStack,
  Menu,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  memo,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from "react";
import type { Situation } from "@/generated/fastapi";
import { CONTENT_ITEM_FOCUS_VISIBLE_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_HOVER_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import ContentItemCard from "@/shared/ui/display/ContentItemCard";
import { ContentItemPreviewActions } from "@/shared/ui/display/ContentItemPrimitives";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import type { ContentItemAction } from "@/shared/ui/display/ContentItemPrimitives";
import { iconSizePresets } from "@/shared/config/iconSizePresets";
import { useSystemDesign } from "@/shared/system-design";
import useSituationHeaderItemGestures from "../hooks/useSituationHeaderItemGestures";
import SituationHeader from "./SituationHeader";

type SituationHeaderItemProps = {
  situation: Situation;
  isSelected: boolean;
  actions: ContentItemAction[];
  primaryAction?: ContentItemAction;
  primaryMenuActions?: ContentItemAction[];
  isPrimaryActionHighlighted?: boolean;
  mobileActions?: ContentItemAction[];
  onOpenDetail?: () => void;
};

type SituationHeaderPrimaryActionMenuProps = {
  action: ContentItemAction;
  isMobileDevice: boolean;
  menuActions: ContentItemAction[];
  isHighlighted?: boolean;
  onOpenChange?: (details: { open: boolean }) => void;
};

function SituationHeaderPrimaryActionMenu({
  action,
  isMobileDevice,
  menuActions,
  isHighlighted = false,
  onOpenChange,
}: SituationHeaderPrimaryActionMenuProps) {
  const borderColor = isHighlighted
    ? (action.color ?? "moveon.primary")
    : "moveon.surface.border";

  return (
    <Menu.Root
      positioning={{
        placement: isMobileDevice ? "bottom" : "bottom-start",
      }}
      lazyMount={true}
      size="sm"
      onOpenChange={onOpenChange}
    >
      <Menu.Trigger asChild>
        <Button
          size="sm"
          variant="ghost"
          borderWidth="1"
          borderStyle="solid"
          borderColor={borderColor}
          color={action.color ?? "moveon.primary"}
          bg={isHighlighted ? "moveon.primaryLight" : "moveon.surface.bg"}
          outlineWidth={isHighlighted ? "1" : "0"}
          outlineStyle="solid"
          outlineColor={action.color ?? "moveon.primary"}
          h={{ base: "9", lg: "8" }}
          minW={{ base: "12", lg: "8" }}
          px="2"
          aria-label={action.humanReadable}
          title={action.humanReadable}
          _hover={{
            bg: isHighlighted
              ? "moveon.primaryLight"
              : "moveon.surface.elevatedBg",
          }}
        >
          <GenericIcon
            icon={action.icon}
            size={iconSizePresets.inline}
            color={action.color ?? "moveon.primary"}
            humanReadable={action.humanReadable}
          />
          <Text
            as="span"
            display={{ base: "none", lg: "inline" }}
            fontSize="xs"
            fontWeight="medium"
          >
            {action.label}
          </Text>
          <GenericIcon
            icon="expand_more"
            size="xs"
            color={action.color ?? "moveon.primary"}
            humanReadable="Open shape menu"
          />
        </Button>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            bg="moveon.surface.toolbarBg"
            color="moveon.surface.fg"
            borderWidth="1"
            borderColor="moveon.surface.border"
            boxShadow="lg"
            minW={{ base: "72", sm: "80" }}
            maxW="[calc(100vw - 2rem)]"
            zIndex="max"
          >
            {menuActions.map((menuAction) => {
              const actionColor = menuAction.disabled
                ? "moveon.text.muted"
                : menuAction.tone === "danger"
                  ? "moveon.danger"
                  : (menuAction.color ?? "moveon.surface.fg");
              const iconColor = menuAction.disabled
                ? "moveon.text.muted"
                : menuAction.tone === "danger"
                  ? "moveon.danger"
                  : (menuAction.color ?? "moveon.primary");

              return (
                <Menu.Item
                  key={menuAction.key}
                  value={menuAction.key}
                  onClick={menuAction.onClick}
                  disabled={menuAction.disabled}
                  title={menuAction.humanReadable}
                  aria-label={menuAction.humanReadable}
                  color={actionColor}
                  _hover={{
                    bg: "moveon.surface.elevatedBg",
                  }}
                >
                  <HStack align="flex-start" gap="3" minW="0">
                    <Box flexShrink="0" pt="0.5">
                      <GenericIcon
                        icon={menuAction.icon}
                        size={iconSizePresets.menuItem}
                        color={iconColor}
                        humanReadable={menuAction.humanReadable}
                      />
                    </Box>
                    <VStack align="stretch" gap="0" minW="0">
                      <Text fontSize="sm" fontWeight="medium">
                        {menuAction.label}
                      </Text>
                      <Text
                        fontSize="xs"
                        color={
                          menuAction.tone === "danger"
                            ? "moveon.danger"
                            : "moveon.text.muted"
                        }
                        lineHeight="short"
                      >
                        {menuAction.humanReadable}
                      </Text>
                    </VStack>
                  </HStack>
                </Menu.Item>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function SituationHeaderItem({
  situation,
  isSelected,
  actions,
  primaryAction,
  primaryMenuActions = [],
  isPrimaryActionHighlighted = false,
  mobileActions,
  onOpenDetail,
}: SituationHeaderItemProps) {
  const { isMobileDevice } = useSystemDesign();
  const backgroundColor = isSelected
    ? "moveon.selection.bg"
    : "moveon.surface.elevatedBg";
  const canOpenDetail = Boolean(onOpenDetail);
  const selectionLabel = isSelected ? "Selected situation" : null;
  const dateLabel = situation.has_started
    ? `${situation.date_from}${
        !situation.is_current
          ? ` - ${situation.date_to}`
          : situation.is_current
            ? " - Ongoing"
            : ""
      }`
    : "Not started yet";
  const stopCardInteractionPropagation = (event: SyntheticEvent) => {
    event.stopPropagation();
  };
  const suppressCardActionRef = useRef(false);
  const suppressCardActionTimeoutRef =
    useRef<ReturnType<typeof setTimeout> | null>(null);
  const actionMenuItems = [
    ...primaryMenuActions,
    ...(mobileActions ?? actions),
  ];
  const handlePrimaryMenuOpenChange = useCallback(
    (details: { open: boolean }) => {
      if (suppressCardActionTimeoutRef.current) {
        clearTimeout(suppressCardActionTimeoutRef.current);
        suppressCardActionTimeoutRef.current = null;
      }

      if (details.open) {
        suppressCardActionRef.current = true;
        return;
      }

      suppressCardActionTimeoutRef.current = setTimeout(() => {
        suppressCardActionRef.current = false;
        suppressCardActionTimeoutRef.current = null;
      }, 350);
    },
    [],
  );
  const handleOpenDetail = useCallback(() => {
    if (suppressCardActionRef.current) {
      return;
    }

    onOpenDetail?.();
  }, [onOpenDetail]);
  const {
    handleCardClick,
    handleCardKeyDown,
    handlePointerDown,
    handlePointerMove,
  } = useSituationHeaderItemGestures({
    canOpenDetail,
    onOpenDetail: handleOpenDetail,
  });
  const primaryMenuActionsForViewport = isMobileDevice
    ? actionMenuItems
    : primaryMenuActions;

  useEffect(() => {
    return () => {
      if (suppressCardActionTimeoutRef.current) {
        clearTimeout(suppressCardActionTimeoutRef.current);
      }
    };
  }, []);

  const card = (
    <ContentItemCard
      as="header"
      role={canOpenDetail ? "button" : undefined}
      tabIndex={canOpenDetail ? 0 : undefined}
      selected={isSelected}
      bg={backgroundColor}
      borderWidth="1"
      cursor={canOpenDetail ? "pointer" : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      _hover={CONTENT_ITEM_HOVER_STYLES}
      _focusVisible={
        canOpenDetail ? CONTENT_ITEM_FOCUS_VISIBLE_STYLES : undefined
      }
    >
      <VStack align="stretch" gap="2" width="full">
        <HStack gap="2" justify="space-between" align="center" width="full">
          <HStack gap="2" minW={0} flex="1" flexWrap="wrap">
            {selectionLabel ? (
              <Badge variant="surface" colorPalette="purple" flexShrink={0}>
                {selectionLabel}
              </Badge>
            ) : null}
            <Text
              fontSize="xs"
              color="moveon.text.muted"
              lineHeight="short"
              minW={0}
              overflowWrap="anywhere"
            >
              {dateLabel}
            </Text>
          </HStack>

          <HStack gap="1" flex="0 0 auto" ms="auto">
            <Box
              display={{ base: "none", lg: "block" }}
              onClick={stopCardInteractionPropagation}
              onPointerDown={stopCardInteractionPropagation}
              onKeyDown={stopCardInteractionPropagation}
            >
              <ContentItemPreviewActions
                actions={actions}
                mobileActions={mobileActions}
              />
            </Box>

            {primaryAction && primaryMenuActionsForViewport.length > 0 ? (
              <Box
                onClick={stopCardInteractionPropagation}
                onPointerDown={stopCardInteractionPropagation}
                onKeyDown={stopCardInteractionPropagation}
              >
                <SituationHeaderPrimaryActionMenu
                  action={primaryAction}
                  isMobileDevice={isMobileDevice}
                  menuActions={primaryMenuActionsForViewport}
                  isHighlighted={isPrimaryActionHighlighted}
                  onOpenChange={handlePrimaryMenuOpenChange}
                />
              </Box>
            ) : null}
          </HStack>
        </HStack>

        <Box minW={0} maxW="full">
          <SituationHeader situation={situation} showDates={false} />
        </Box>
      </VStack>
    </ContentItemCard>
  );

  return card;
}

export default memo(SituationHeaderItem);
